import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Drawer, Empty, Space, Statistic, Table, Tag, Typography,
  Row, Col, Card, Button, message
} from 'antd';
import { ReloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { accountReportsApi } from './accountReportsApi';
import {
  agingSeverityForBucket, bucketLabel, formatAgingAmount,
  pickPartyDisplayName, pickPartySubtitle, friendlyAgingBasisMessage
} from './agingReportHelpers';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Aging Detail Drawer (Stage 6E.3).
//
// Opens from DebtorsAgingPage / CreditorsAgingPage. Shows the
// voucher-level outstanding for one party / ledger, broken into
// aging buckets. Read-only — no posting, no editing.
//
// Props:
//   open: boolean
//   onClose: () => void
//   reportType: 'debtors' | 'creditors'
//   row: the summary row that was clicked (has ledgerAccountId/partyId)
//   asOfDate: report-level asOfDate
// ─────────────────────────────────────────────────────────────────

export default function AgingDetailDrawer({ open, onClose, reportType, row, asOfDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!row || !open) return;
    setLoading(true);
    setLoadError(null);
    try {
      const out = await accountReportsApi.agingDetail({
        reportType,
        ledgerAccountId: row.ledgerAccountId,
        partyId:         row.partyId || null,
        asOfDate
      });
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'aging_detail'));
    } finally { setLoading(false); }
  }, [reportType, row, asOfDate, open]);

  useEffect(() => {
    if (open) fetchDetail();
    if (!open) { setData(null); setLoadError(null); }
  }, [open, fetchDetail]);

  const rows = data?.rows || [];
  const summary = data?.summary || { totalOutstanding: 0, openLineCount: 0 };

  const columns = useMemo(() => ([
    { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo', width: 140,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Type', dataIndex: 'voucherType', key: 'voucherType', width: 110,
      render: (v) => v ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>{v}</Tag> : <Text type="secondary">—</Text> },
    { title: 'Voucher Date', dataIndex: 'voucherDate', key: 'voucherDate', width: 110,
      render: (v) => v ? String(v).slice(0, 10) : '—' },
    { title: 'Due Date', dataIndex: 'dueDate', key: 'dueDate', width: 110,
      render: (v) => v ? String(v).slice(0, 10) : <Text type="secondary">—</Text> },
    { title: 'Narration', dataIndex: 'narration', key: 'narration', ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Original (₹)', dataIndex: 'originalAmount', key: 'originalAmount',
      width: 130, align: 'right',
      render: (v) => formatAgingAmount(v, { alwaysShow: true }) },
    { title: 'Outstanding (₹)', dataIndex: 'outstandingAmount', key: 'outstandingAmount',
      width: 140, align: 'right',
      render: (v) => <strong>{formatAgingAmount(v, { alwaysShow: true })}</strong> },
    { title: 'Age', dataIndex: 'ageDays', key: 'ageDays', width: 80, align: 'right',
      render: (v) => (typeof v === 'number' ? `${v}d` : '—') },
    { title: 'Bucket', dataIndex: 'bucket', key: 'bucket', width: 130,
      render: (v) => (
        <Tag color={agingSeverityForBucket(v)} style={{ marginInlineEnd: 0 }}>
          {bucketLabel(v)}
        </Tag>
      ) }
  ]), []);

  const partyName = row ? pickPartyDisplayName(row) : '';
  const partySub  = row ? pickPartySubtitle(row)   : '';
  const oldestOpen = summary.openLineCount > 0 && rows[0]
    ? String(rows[0].voucherDate).slice(0, 10) : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={1080}
      destroyOnClose
      title={
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Title level={5} style={{ margin: 0 }}>
            {reportType === 'debtors' ? 'Receivable detail' : 'Payable detail'} — {partyName}
          </Title>
          {partySub ? (
            <Text type="secondary" style={{ fontSize: 12 }}>{partySub}</Text>
          ) : null}
        </Space>
      }
      extra={
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchDetail} loading={loading}>
            Refresh
          </Button>
          <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>
            Print
          </Button>
        </Space>
      }
    >
      {loadError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load aging detail"
          description={friendlyAccountsErrorMessage(loadError, 'aging_detail')}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchDetail}>Retry</Button>
          }
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={friendlyAgingBasisMessage(data?.meta)}
      />

      <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic
              title="Total Outstanding (₹)"
              value={summary.totalOutstanding}
              precision={2}
              valueStyle={{
                color: summary.totalOutstanding > 0
                  ? (reportType === 'debtors' ? '#cf1322' : '#0958d9')
                  : '#3f8600'
              }}
            />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic title="Open Items" value={summary.openLineCount} precision={0} />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small">
            <div style={{ fontSize: 11.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.2, fontWeight: 500 }}>
              Oldest Open
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: '#0f172a' }}>
              {oldestOpen || <Text type="secondary">—</Text>}
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
              As of {asOfDate || (data?.meta?.asOfDate || '—')}
            </div>
          </Card>
        </Col>
      </Row>

      <Table
        size="small"
        rowKey={(r) => `${r.voucherId}-${r.voucherNo}`}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 25, size: 'small' }}
        locale={{ emptyText: <Empty description={friendlyAccountsEmptyMessage('aging_detail')} /> }}
      />
    </Drawer>
  );
}
